// ---------------------------------------------------------------------------
// Deciding whether a push failure is worth retrying.
//
// Pure, and separate from sync.ts, because getting this wrong has two bad
// shapes: retrying forever wedges a shift's uploads behind one bad row, and
// giving up too readily throws away work over a dropped connection.
// ---------------------------------------------------------------------------

/**
 * True when the server will reject this identically next time.
 *
 * P0001 is `raise exception` from our own security definer functions --
 * "forbidden", "that address is already on the list", "outside the turf".
 * Class 23 is an integrity violation: unique, foreign key, check constraint.
 * PGRST204 is PostgREST's own: a column in the payload that the table does not
 * have. 42501 is a row-level security rejection: the row names a home that is
 * no longer in the canvasser's turf -- reassigned, or taken out of the walk
 * universe by scripts/scope-to-district.ts while the phone still had it
 * cached. None of these improves by waiting.
 *
 * Everything else -- no code at all, a timeout, a 5xx -- is assumed
 * retryable, which is the safe way to be wrong: the row stays queued and the
 * canvasser keeps their work.
 *
 * Note what "permanent" buys, because it is narrower than the word suggests:
 * the batch is stepped over so the rest of the sync proceeds. The row is not
 * discarded and not marked synced -- it stays queued at synced = 0 and is
 * retried on every later sync. So misclassifying a transient failure as
 * permanent costs a delay, while the reverse costs everything: syncNow() lets
 * a throw out of push(), which skips the pull and mark_synced() with it. One
 * unshiftable row then stops the device receiving turf, other canvassers'
 * visits and do-not-contact flags, for as long as it sits there. That is how
 * PGRST204 came to be on this list.
 */
export function isPermanent(error: { code?: string | null } | null | undefined): boolean {
  const code = error?.code;
  if (!code) return false;
  return (
    code === 'P0001' || code === 'PGRST204' || code === '42501' || code.startsWith('23')
  );
}

/** First wait after a permanent rejection; doubles per attempt from there. */
export const RETRY_BASE_MS = 60_000;
/** Longest wait. A rejected row is still tried at least this often. */
export const RETRY_MAX_MS = 60 * 60_000;

/**
 * How long to leave a row the server rejected outright, after its
 * `attempts`-th failure.
 *
 * Permanent is not forever: a home that left the turf can come back, and a
 * migration can start accepting what it refused (20261027000100 did, for
 * visits at inactive homes). So such a row is still retried, but on a doubling
 * delay instead of every sync. Before this, 39 visits one canvasser could not
 * upload were each sent on their own every 25 seconds, all shift.
 */
export function retryDelay(attempts: number): number {
  const doublings = Math.min(Math.max(attempts, 1) - 1, 30);
  return Math.min(RETRY_BASE_MS * 2 ** doublings, RETRY_MAX_MS);
}

/** Whether a queued row should be sent now. `force` is "Sync now" on /sync. */
export function isDue(row: { retry_at?: number }, now: number, force: boolean): boolean {
  return force || row.retry_at === undefined || row.retry_at <= now;
}
